import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiCoreAdapter from '../../../../../hooks/apiCoreAdapter';
import { usePsychologistContext } from '../../../../../contexts/PsychologistContext';
import { useAuth } from '../../../../../contexts/AuthContext';
import type { SessionLite } from '../../../../../types/session-lite';
import type { DayInfo } from '../types';
import { parseSessionDate, toISODate, getWeekStart, getMiddleDay } from '../../../../../utils/sessions';
import { BA_TIMEZONE } from '../../../../../utils/timezone';
import { fetchNoScheduleMap, type NoScheduleEntry } from '../../../../../utils/tallyNoSchedule';
import { fetchPatientsOnce, fetchPatientFullDataOnce, fetchLeadProcessIds } from '../../../../../components/psychologist/calendar/data';

/**
 * useSessionsData manages all data-fetching and derived state for the Sessions
 * tab: loading sessions from the API for the visible week, building the weekday
 * grid, computing "next session" markers, and detecting patients with no
 * follow-up session scheduled.
 */

const WEEKDAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

// Re-export utilities so existing importers of this module continue to work.
export { parseSessionDate, toISODate, getWeekStart, getMiddleDay } from '../../../../../utils/sessions';

const buildWeekDays = (weekStart: Date, sessionsByDay: Record<string, SessionLite[]>): DayInfo[] => {
  const today = toISODate(new Date());
  const days: DayInfo[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    const iso = toISODate(d);
    const count = (sessionsByDay[iso] || []).filter((s) => !s.isCancelled).length;
    days.push({
      dateISO: iso,
      dayNumber: d.getDate(),
      weekdayName: WEEKDAY_NAMES[d.getDay()],
      isToday: iso === today,
      sessionsCount: count,
    });
  }
  return days;
};

export type PatientMissingEntry = {
  patientId?: string;
  patientName: string;
  email?: string;
  processId?: string;
  lastSessionDate?: string;
};

export type PatientHandledEntry = PatientMissingEntry & { reason: string };

type UseSessionsDataResult = {
  sessionsLoaded: boolean;
  isLoading: boolean;
  allSessions: SessionLite[];
  sessionsByDay: Record<string, SessionLite[]>;
  weekDays: DayInfo[];
  sessionsForDay: SessionLite[];
  weekStart: Date;
  selectedISO: string;
  setSelectedISO: (iso: string) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
  isCurrentWeek: boolean;
  onViewProcess: (sessionId: string, onPatientClick: (patientId: string, processId?: string, patientName?: string, therapyType?: string) => void) => void;
  refreshSessions: () => void;
  patientsMissingNext: PatientMissingEntry[];
  patientsHandled: PatientHandledEntry[];
  missingNextModalOpen: boolean;
  openMissingNextModal: () => void;
  dismissMissingNextModal: () => void;
};

export function useSessionsData(): UseSessionsDataResult {
  const { loadServices, psychologist } = usePsychologistContext();
  const { psychologistId } = useAuth();
  const therapistTz = psychologist?.timezone || BA_TIMEZONE;
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [allSessions, setAllSessions] = useState<SessionLite[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  // Deep-link compartible: ?date=YYYY-MM-DD posiciona la semana y el día seleccionado
  const dateParam = searchParams.get('date');
  const initialDate = dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) && !Number.isNaN(new Date(`${dateParam}T12:00:00`).getTime())
    ? dateParam
    : null;
  const [weekStart, setWeekStart] = useState<Date>(() =>
    getWeekStart(initialDate ? new Date(`${initialDate}T12:00:00`) : new Date()),
  );
  const [selectedISO, setSelectedISO] = useState<string>(() =>
    initialDate ?? new Date().toLocaleDateString('en-CA', { timeZone: therapistTz }),
  );
  const [sessionsByDay, setSessionsByDay] = useState<Record<string, SessionLite[]>>({});
  const [missingNextModalOpen, setMissingNextModalOpen] = useState(false);
  const [patientsMissingNext, setPatientsMissingNext] = useState<PatientMissingEntry[]>([]);
  const [patientsHandled, setPatientsHandled] = useState<PatientHandledEntry[]>([]);
  const [missingNextDismissedAt, setMissingNextDismissedAt] = useState(0);

  // Load services once on mount.
  const servicesCalledRef = useRef(false);
  useEffect(() => {
    if (!servicesCalledRef.current) {
      servicesCalledRef.current = true;
      loadServices();
    }
  }, [loadServices]);

  // Load sessions for the visible week whenever weekStart changes.
  useEffect(() => {
    let cancelled = false;
    setSessionsLoaded(true);

    async function loadSessions() {
      try {
        setIsLoading(true);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);
        const fromDate = toISODate(weekStart);
        const toDate = toISODate(weekEnd);

        // Run sessions fetch and patient list in parallel.
        // fetchPatientsOnce is in-memory cached after first call, so subsequent weeks
        // incur zero network cost. The patients list gives us timezone data that the
        // by-day endpoint does not include in its response.
        const [response, patients] = await Promise.all([
          apiCoreAdapter.psychologistSessionsByDay({
            from: `${fromDate} 00:00:00`,
            to: `${toDate} 23:59:59`,
            status: 'active,incomplete,settled,cancelled,free',
          }),
          psychologistId ? fetchPatientsOnce(psychologistId) : Promise.resolve([]),
        ]);

        // Build timezone lookup maps (id → tz, process → tz) from the patients list.
        const tzById = new Map<string, string>();
        const tzByProcess = new Map<string, string>();
        patients.forEach(p => {
          if (p.timezone) {
            if (p.id) tzById.set(p.id, p.timezone);
            if (p.process) tzByProcess.set(p.process, p.timezone);
          }
        });

        if (!cancelled) {
          if (response && typeof response === 'object') {
            // Regroup by the psychologist's local timezone date.
            const regrouped: Record<string, SessionLite[]> = {};
            Object.values(response).forEach((daySessions) => {
              if (!Array.isArray(daySessions)) return;
              daySessions.forEach((session) => {
                const localDate = parseSessionDate(session.date).toLocaleDateString('en-CA', {
                  timeZone: therapistTz,
                });
                if (!regrouped[localDate]) regrouped[localDate] = [];
                regrouped[localDate].push(session);
              });
            });

            const now = new Date();
            const updatedByDay: Record<string, SessionLite[]> = {};

            // Primera pasada: mapear sesiones con isFirst, sin isNext aún
            Object.entries(regrouped).forEach(([dayISO, daySessions]) => {
              if (!Array.isArray(daySessions)) { updatedByDay[dayISO] = []; return; }
              updatedByDay[dayISO] = daySessions.map((s) => {
                const isFirstFromApi = (s as unknown as { first_session?: boolean }).first_session;
                const isFirst =
                  typeof isFirstFromApi === 'boolean'
                    ? isFirstFromApi
                    : (s as unknown as { firstSession?: boolean }).firstSession ?? s.isFirst;
                // 1. Try to extract timezone directly from the API response fields.
                const rawPatientTz: string | undefined =
                  (s as unknown as { patients?: Array<{ timezone?: string }> }).patients?.[0]?.timezone
                  ?? (s as unknown as { patient?: { timezone?: string } }).patient?.timezone
                  ?? (s as unknown as { patient_timezone?: string }).patient_timezone
                  ?? (s as unknown as { patientTimezone?: string }).patientTimezone
                  ?? undefined;
                // 2. Fall back to the patients list keyed by patient ID or process ID.
                const rawId = s.patientId ?? (s as unknown as { patient_id?: string }).patient_id;
                const enrichedTz = rawPatientTz
                  || (rawId ? tzById.get(rawId) : undefined)
                  || (s.process ? tzByProcess.get(s.process) : undefined);
                // El timeLabel del backend viene en hora del servidor (UTC) → recalcular en la hora de la psicóloga
                const arTimeLabel = parseSessionDate(s.date)
                  .toLocaleTimeString('en-US', { timeZone: therapistTz, hour: 'numeric', minute: '2-digit', hour12: true })
                  .toLowerCase();
                return { ...s, timeLabel: arTimeLabel, isFirst, isNext: false, joinable: false, ...(enrichedTz ? { patientTimezone: enrichedTz } : {}) };
              });
            });

            // Encontrar la única sesión globalmente próxima (la primera desde ahora)
            const globalNext = Object.values(updatedByDay)
              .flat()
              .filter((s) => !s.isCancelled && ['active', 'incomplete', 'settled'].includes(s.status))
              .filter((s) => parseSessionDate(s.date).getTime() > now.getTime())
              .sort((a, b) => parseSessionDate(a.date).getTime() - parseSessionDate(b.date).getTime())[0];

            // Segunda pasada: asignar isNext solo a esa sesión y joinable por ventana de 6h
            const sixHoursMs = 6 * 3600 * 1000;
            Object.keys(updatedByDay).forEach((dayISO) => {
              updatedByDay[dayISO] = updatedByDay[dayISO].map((s) => {
                const diffMs = parseSessionDate(s.date).getTime() - now.getTime();
                return {
                  ...s,
                  isNext: !!globalNext && s.id === globalNext.id,
                  joinable: !s.isCancelled && diffMs >= 0 && diffMs <= sixHoursMs,
                };
              });
            });

            setSessionsByDay(updatedByDay);
            const flat: SessionLite[] = [];
            Object.values(updatedByDay).forEach((ds) => { if (Array.isArray(ds)) flat.push(...ds); });
            setAllSessions(flat);
          } else {
            setSessionsByDay({});
            setAllSessions([]);
          }
          setIsLoading(false);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading sessions:', error);
          setSessionsByDay({});
          setAllSessions([]);
          setIsLoading(false);
        }
      }
    }

    loadSessions();
    return () => { cancelled = true; };
  }, [weekStart, psychologistId, refreshKey, therapistTz]);

  const weekDays = useMemo(() => {
    const days = buildWeekDays(weekStart, sessionsByDay);
    if (sessionsLoaded) {
      const dayExists = days.some((d) => d.dateISO === selectedISO);
      if (!dayExists) setSelectedISO(getMiddleDay(weekStart));
    }
    return days;
  }, [weekStart, sessionsByDay, selectedISO, sessionsLoaded]);

  const sessionsForDay = useMemo(() => sessionsByDay[selectedISO] || [], [sessionsByDay, selectedISO]);

  // Reflejar el día seleccionado en ?date= (replace). Se omite el montaje para no
  // ensuciar la URL al entrar sin parámetro; el deep-link inicial ya viene en la URL.
  const dateSyncMountedRef = useRef(false);
  useEffect(() => {
    if (!dateSyncMountedRef.current) { dateSyncMountedRef.current = true; return; }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('date', selectedISO);
      return next;
    }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedISO]);

  // Fetch patients with no next session using the same endpoint as the Patients tab.
  // Both the patients call and the Make webhook call fire in parallel to minimise
  // total wait time. Make results are cached in sessionStorage for 5 minutes.
  useEffect(() => {
    if (!psychologistId) return;
    let cancelled = false;
    async function loadMissingNext() {
      try {
        const psychologistEmail = (() => {
          try {
            const raw = sessionStorage.getItem('psi-session-data') || localStorage.getItem('psi-session-data');
            return raw ? (JSON.parse(raw)?.user?.email ?? '') : '';
          } catch { return ''; }
        })();

        const sinceISO = (() => {
          const d = new Date();
          d.setDate(d.getDate() - 90);
          return d.toISOString();
        })();

        // 1 solo fetch (compartido/cacheado con fetchPatientsOnce vía data.ts) en vez de
        // 2 llamados separados por status=withoutSession / status=inactive: los buckets
        // ya vienen calculados por el backend, se clasifica en memoria.
        const [fullData, noScheduleMap, leadProcessIds] = await Promise.all([
          fetchPatientFullDataOnce(psychologistId ?? ''),
          psychologistEmail
            ? fetchNoScheduleMap(psychologistEmail, sinceISO)
            : Promise.resolve(new Map<string, NoScheduleEntry>()),
          fetchLeadProcessIds(psychologistId ?? ''),
        ]);

        if (cancelled) return;

        // status_buckets marca "withoutsession" con solo mirar si alguna vez tuvo sesiones
        // settled, así que un ex-paciente que volvió y está de nuevo en un estado del
        // segmento lead (ej. "Horarios ofrecidos") igual cae ahí. Se excluye explícitamente
        // por process_id contra el estado real, no por cantidad de sesiones históricas.
        const results = fullData.filter(
          (r) => (r.status_buckets?.includes('withoutsession') ?? false)
            && !(r.status_buckets?.includes('inactive') ?? false)
            && r.scheduling_eligible !== false
            && !r.next_session_date
            && !(r.process_id && leadProcessIds.has(r.process_id))
        );
        const rawList = results
          .map((r: Record<string, unknown>) => ({
            patientId: r.id as string | undefined,
            patientName: [r.name, r.surname].filter(Boolean).join(' ').trim() || 'Sin nombre',
            email: (r.email as string) ?? undefined,
            processId: (r.process_id as string | undefined) || (r.id as string | undefined),
            lastSessionDate: (r.last_session_date as string) ?? undefined,
          }))
          .sort((a, b) => {
            if (!a.lastSessionDate) return 1;
            if (!b.lastSessionDate) return -1;
            return a.lastSessionDate.localeCompare(b.lastSessionDate);
          });

        // A patient is "handled" only if the Tally form was submitted AFTER their
        // last session — meaning the psychologist managed the situation post-session.
        // If the patient had new sessions after the form was submitted, they fall
        // back to "pending" so the psychologist is prompted to act again.
        const isHandled = (processId: string | undefined, lastSessionDate: string | undefined): boolean => {
          if (!processId) return false;
          const entry = noScheduleMap.get(processId);
          if (!entry) return false;
          // If we don't have submittedAt, fall back to just checking presence in the map
          if (!entry.submittedAt || !lastSessionDate) return true;
          return entry.submittedAt > lastSessionDate;
        };

        const filteredList = noScheduleMap.size > 0
          ? rawList.filter((p) => !isHandled(p.processId, p.lastSessionDate))
          : rawList;

        const handled: PatientHandledEntry[] = noScheduleMap.size > 0
          ? rawList
              .filter((p) => isHandled(p.processId, p.lastSessionDate))
              .map((p) => ({ ...p, reason: noScheduleMap.get(p.processId!)?.reason ?? '' }))
          : [];

        setPatientsHandled(handled);
        setPatientsMissingNext(filteredList);
        if (filteredList.length > 0 && Date.now() - missingNextDismissedAt > 60000) {
          setMissingNextModalOpen(true);
        }
      } catch {
        // Si falla, no mostramos el modal
      }
    }
    loadMissingNext();
    return () => { cancelled = true; };
  }, [missingNextDismissedAt, psychologistId]);

  const onPrevWeek = () => {
    const prev = new Date(weekStart);
    prev.setDate(prev.getDate() - 7);
    setWeekStart(prev);
    setSelectedISO(getMiddleDay(prev));
  };

  const onNextWeek = () => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + 7);
    setWeekStart(next);
    setSelectedISO(getMiddleDay(next));
  };

  /** Navega a la semana actual y selecciona el día de hoy. */
  const onToday = () => {
    const now = new Date();
    const todayWeekStart = getWeekStart(now);
    const todayISO = now.toLocaleDateString('en-CA', { timeZone: therapistTz });
    setWeekStart(todayWeekStart);
    setSelectedISO(todayISO);
  };

  /** true cuando la semana visible ya contiene el día de hoy. */
  const isCurrentWeek = useMemo(() => {
    const todayWeekStart = getWeekStart(new Date());
    return toISODate(weekStart) === toISODate(todayWeekStart);
  }, [weekStart]);

  const onViewProcess = (
    sessionId: string,
    onPatientClick: (patientId: string, processId?: string, patientName?: string, therapyType?: string) => void,
  ) => {
    let session: SessionLite | undefined;
    Object.values(sessionsByDay).forEach((daySessions) => {
      if (Array.isArray(daySessions)) {
        const found = daySessions.find((s) => s.id === sessionId);
        if (found) session = found;
      }
    });
    if (session?.patientId) {
      onPatientClick(session.patientId, session.process, session.patientName, session.therapyType);
    } else if (session?.process) {
      onPatientClick(session.process, session.process, session.patientName, session.therapyType);
    }
  };

  const openMissingNextModal = () => setMissingNextModalOpen(true);

  const dismissMissingNextModal = () => {
    setMissingNextModalOpen(false);
    setMissingNextDismissedAt(Date.now());
  };

  const refreshSessions = () => setRefreshKey(k => k + 1);

  return {
    sessionsLoaded,
    isLoading,
    allSessions,
    sessionsByDay,
    weekDays,
    sessionsForDay,
    weekStart,
    selectedISO,
    setSelectedISO,
    onPrevWeek,
    onNextWeek,
    onToday,
    isCurrentWeek,
    onViewProcess,
    refreshSessions,
    patientsMissingNext,
    patientsHandled,
    missingNextModalOpen,
    openMissingNextModal,
    dismissMissingNextModal,
  };
}
